import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Badge, Button, Card, ProgressBar, Spinner } from "react-bootstrap";
import { Table, Column, Cell, HeaderCell } from "rsuite-table";
import moment from "moment";
import RecertificationStart from "./RecertificationStart";
import useScreenSize from "../../../hooks/useScreenSize";
import { getCycles } from "../../../services/recertification";

export const formatDay = (value) =>
  value ? moment(value).format("MM/DD/YYYY") : "";

export const percentOf = (part, whole) =>
  whole ? Math.round((part / whole) * 100) : 0;

function Recertification() {
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { height } = useScreenSize();
  const startRef = useRef({ open() {} });
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    getCycles()
      .then(setCycles)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  return (
    <>
      <RecertificationStart
        ref={startRef}
        onStarted={(cycle) => navigate(`/recertification/${cycle.id}`)}
      />

      <Card className="mb-3">
        <Card.Body className="d-flex justify-content-between align-items-center">
          <div>
            <Card.Title className="mb-1">Recertification</Card.Title>
            <Card.Text className="text-muted mb-0">
              Deactivate a location&rsquo;s families and let them re-enroll by
              phone. Messages go out in batches that you control.
            </Card.Text>
          </div>
          <Button
            variant="primary"
            className="ms-3 text-nowrap"
            onClick={() => startRef.current.open()}
          >
            Start Recertification
          </Button>
        </Card.Body>
      </Card>

      {loading && !cycles.length ? (
        <div className="text-center p-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <Table
          height={Math.max(height - 260, 360)}
          data={cycles}
          rowHeight={62}
          locale={{ emptyMessage: "Nothing has been run yet" }}
        >
          <Column flexGrow={1.5}>
            <HeaderCell>Recertification</HeaderCell>
            <Cell>
              {(row) => (
                <span>
                  <Link to={`/recertification/${row.id}`}>{row.name}</Link>
                  {row.isTest && (
                    <Badge bg="warning" text="dark" className="ms-2">
                      Test
                    </Badge>
                  )}
                </span>
              )}
            </Cell>
          </Column>
          <Column flexGrow={1}>
            <HeaderCell>Location</HeaderCell>
            <Cell dataKey="locationName" />
          </Column>
          <Column flexGrow={0.7}>
            <HeaderCell>Status</HeaderCell>
            <Cell>
              {(row) => (
                <Badge bg={row.statusName === "Open" ? "success" : "dark"}>
                  {row.statusName}
                </Badge>
              )}
            </Cell>
          </Column>
          <Column flexGrow={1.1}>
            <HeaderCell>Window</HeaderCell>
            <Cell>
              {(row) => (
                <span>
                  {formatDay(row.startedOn)} &ndash; {formatDay(row.endDate)}
                  {row.statusName === "Open" && row.daysRemaining != null && (
                    <>
                      <br />
                      <small className="text-muted">
                        {row.daysRemaining} day
                        {row.daysRemaining === 1 ? "" : "s"} left
                      </small>
                    </>
                  )}
                </span>
              )}
            </Cell>
          </Column>
          <Column flexGrow={1.2}>
            <HeaderCell>Messages sent</HeaderCell>
            <Cell>
              {(row) => {
                const p = row.progress;
                const pct = percentOf(p.inviteSent, p.familiesInScope);
                return (
                  <div>
                    <ProgressBar
                      now={pct}
                      variant="info"
                      style={{ height: "8px" }}
                    />
                    <small className="text-muted">
                      {p.inviteSent.toLocaleString()} of{" "}
                      {p.familiesInScope.toLocaleString()}
                    </small>
                  </div>
                );
              }}
            </Cell>
          </Column>
          <Column flexGrow={1.2}>
            <HeaderCell>Reactivated</HeaderCell>
            <Cell>
              {(row) => {
                const p = row.progress;
                const pct = percentOf(p.reactivated, p.familiesInScope);
                return (
                  <div>
                    <ProgressBar
                      now={pct}
                      variant="success"
                      style={{ height: "8px" }}
                    />
                    <small className="text-muted">
                      {p.reactivated.toLocaleString()} of{" "}
                      {p.familiesInScope.toLocaleString()} ({pct}%)
                    </small>
                  </div>
                );
              }}
            </Cell>
          </Column>
        </Table>
      )}
    </>
  );
}

export default Recertification;
